package top.wcpe.allincore.dsl.compiler.type

import top.wcpe.allincore.dsl.compiler.api.ProjectTypeTable
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst

/**
 * 局部 var 类型推断器（D2-a 引入，runtime 暂不消费）。
 *
 * 输入：action / fun / 回调体的 [StmtAst] 序列
 * 输出：`var name -> IrType` 的最后一次推断结果
 *
 * 推断规则（推不出一律 [IrType.AnyT]）：
 * - **字面量**：Number / String / Bool / null（Nullable(Any)）/ 列表字面量 / Map 字面量 /
 *   Range（按迭代器形态承载为 ListT）
 * - **引用**：LocalRef 从 [env] 查表；其它命名空间引用（state / theme / props 等）暂归 AnyT
 * - **一元 / 二元**：
 *   - `+`：任一侧为 String 结果 String，否则 Number
 *   - `- * / %`：Number
 *   - `< > <= >= == != && ||`：Boolean
 *   - `!`：Boolean，`-`：Number
 *   - `is`：Boolean
 * - **控制流**：Ternary / Elvis / IfExpr / WhenExpr 取分支类型并集
 *   （任一为 AnyT 取另一个；Nullable(AnyT) + T → Nullable(T)；其它不一致退化为 AnyT）
 * - **调用 / 后缀链**：MemberCall 末端方法名 number / string / bool / map / list 对应类型；
 *   SafeMemberCall 包 Nullable；SafeMemberAccess / IndexExpr 当 AnyT 处理
 *
 * 重复声明：覆盖式更新（取最近一次 inferred）。
 *
 * **跨语句类型流动**（M8 阶段 D 新增）：[infer] 在 walk 语句时把当前已推断的 var 类型作为
 * 后续表达式的环境，让 `var x = 1; var y = x + 1` 中的 y 能正确推断为 NumberT。
 * 同名 var / val 重复声明覆盖前者；二次赋值（`x = "abc"`）也会刷新当前类型——但**类型一致性
 * 校验**（声明为 Number 后被赋字符串）由 [top.wcpe.allincore.dsl.compiler.semantic.TypeConstraintChecker]
 * 单独负责，不在本对象职责内。
 *
 * **lambda body 内的 var**（M8 阶段 D cleanup 修正）：[visit] 在 ExprStmt / AssignStmt /
 * VarDeclStmt / ReturnStmt 等表达式承载位置上会**穿透到表达式内嵌的 lambda body / IfExpr /
 * WhenExpr 分支体**，把内部 var 推断结果**展平合并**到主 env 表。这是为了让 hover 类型反查
 * 能看到 `state.x = onClick { event -> var local = ... }` 中 local 的类型；它与"作用域屏蔽"
 * 的语言学语义相反——同名 var 跨 lambda 边界会互相覆盖，但实际 .allin 项目几乎不存在跨
 * lambda 的同名 var 写法，简化策略可接受。
 *
 * **跨文件类型流动**（M10 Stage 3 新增）：所有 `infer` / `inferExpr` 都接受可选的
 * [ProjectTypeTable] 参数，由 IDE 端从 stub index 收集后传入。当 LocalRef / CallExpr 在 env
 * 中未命中或 MemberAccess 的 target 是 [IrType.DataInstance] 时，会反查 [ProjectTypeTable]
 * 拿到精确类型。旧 2 参签名内部委托新 3 参签名传 [ProjectTypeTable.EMPTY]，向后兼容。
 */
object TypeInferencer {

    /** 旧入口：不带跨文件类型表（向后兼容）。 */
    fun infer(stmts: List<StmtAst>): Map<String, IrType> = infer(stmts, ProjectTypeTable.EMPTY)

    /**
     * 扫描语句列表，收集所有 [StmtAst.VarDeclStmt] 与 [StmtAst.AssignStmt]（LocalLValue）。
     *
     * @param crossFileTypes 跨文件类型表；为 [ProjectTypeTable.EMPTY] 时与旧 2 参签名等价。
     */
    fun infer(stmts: List<StmtAst>, crossFileTypes: ProjectTypeTable): Map<String, IrType> {
        val result = LinkedHashMap<String, IrType>()
        visit(stmts, result, crossFileTypes)
        return result
    }

    private fun visit(stmts: List<StmtAst>, env: MutableMap<String, IrType>, crossFile: ProjectTypeTable) {
        for (s in stmts) visit(s, env, crossFile)
    }

    private fun visit(stmt: StmtAst, env: MutableMap<String, IrType>, crossFile: ProjectTypeTable) {
        when (stmt) {
            is StmtAst.VarDeclStmt -> {
                // 先扫初始化表达式中嵌入的 lambda body / IfExpr / WhenExpr 内部 var——这样
                // `var x = onClick { event -> var local = 1 }` 中的 local 也能进入 env。
                visitExprForVars(stmt.initializer, env, crossFile)
                env[stmt.name] = inferExpr(stmt.initializer, env, crossFile)
            }
            is StmtAst.AssignStmt -> {
                visitExprForVars(stmt.rhs, env, crossFile)
                val lv = stmt.lhs
                if (lv is LValueAst.LocalLValue) {
                    env[lv.name] = inferExpr(stmt.rhs, env, crossFile)
                }
            }
            is StmtAst.ExprStmt -> visitExprForVars(stmt.call, env, crossFile)
            is StmtAst.ReturnStmt -> stmt.value?.let { visitExprForVars(it, env, crossFile) }
            is StmtAst.IfStmt -> {
                visitExprForVars(stmt.cond, env, crossFile)
                visit(stmt.thenBody, env, crossFile); visit(stmt.elseBody, env, crossFile)
            }
            is StmtAst.WhileStmt -> {
                visitExprForVars(stmt.cond, env, crossFile)
                visit(stmt.body, env, crossFile)
            }
            is StmtAst.ForInStmt -> {
                visitExprForVars(stmt.iterable, env, crossFile)
                visit(stmt.body, env, crossFile)
            }
            is StmtAst.TryStmt -> {
                visit(stmt.tryBody, env, crossFile); visit(stmt.catchBody, env, crossFile); visit(stmt.finallyBody, env, crossFile)
            }
            // batch 语句：递归收集 body 内 var 绑定（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> visit(stmt.body, env, crossFile)
            is StmtAst.WhenStmt -> {
                visitExprForVars(stmt.whenExpr.subject, env, crossFile)
                for (b in stmt.whenExpr.branches) {
                    b.values.forEach { visitExprForVars(it, env, crossFile) }
                    visit(b.body, env, crossFile)
                }
                stmt.whenExpr.elseBody?.let { visit(it, env, crossFile) }
            }
            // 嵌套 fun：fun 边界——内层 var 不污染外层 env。
            // 此处不递归进 fun body（外层 env 表只承载当前作用域）；fun body 内的类型推断由
            // 调用方独立调 [infer]（通过 IdeFacade.analyzeTypes 内 walkVarDecls 的 FunDeclStmt
            // 分支进入嵌套 fun 体并独立 infer）。
            is StmtAst.FunDeclStmt -> Unit
            // Break / Continue 不产生 var 绑定。
            else -> Unit
        }
    }

    /**
     * 在表达式 [expr] 子树内找 lambda body / IfExpr 分支体 / WhenExpr 分支体，把它们各自的
     * var 推断结果合并到 [env]。
     *
     * **作用域简化策略**：lambda 形参 / 块内 var 的"严格隔离"会让 hover 时找不到嵌套类型；
     * 这里采用"展平合并"——lambda body 内的 var 推断结果直接进外层 env，让外层后续表达式
     * 也能引用（这与"作用域屏蔽外层"的语言学语义相反，但与 hover 类型反查的实际需求一致）。
     * 同名 var 取最近一次推断结果。
     *
     * 仅深入需要"穿透到子语句 / 子表达式"的形态（lambda / if / when / 算术 / 调用 / interp
     * 等）；叶子表达式（字面量 / Ref）直接返回。
     */
    private fun visitExprForVars(expr: ExprAst, env: MutableMap<String, IrType>, crossFile: ProjectTypeTable) {
        when (expr) {
            is ExprAst.LambdaExpr -> visit(expr.body, env, crossFile)
            is ExprAst.IfExpr -> {
                visitExprForVars(expr.cond, env, crossFile)
                visit(expr.thenBody, env, crossFile)
                visit(expr.elseBody, env, crossFile)
            }
            is ExprAst.WhenExpr -> {
                visitExprForVars(expr.subject, env, crossFile)
                expr.branches.forEach { br ->
                    br.values.forEach { visitExprForVars(it, env, crossFile) }
                    visit(br.body, env, crossFile)
                }
                expr.elseBody?.let { visit(it, env, crossFile) }
            }
            is ExprAst.BinaryOp -> {
                visitExprForVars(expr.left, env, crossFile); visitExprForVars(expr.right, env, crossFile)
            }
            is ExprAst.UnaryOp -> visitExprForVars(expr.operand, env, crossFile)
            is ExprAst.Ternary -> {
                visitExprForVars(expr.cond, env, crossFile)
                visitExprForVars(expr.thenExpr, env, crossFile)
                visitExprForVars(expr.elseExpr, env, crossFile)
            }
            is ExprAst.Elvis -> {
                visitExprForVars(expr.left, env, crossFile); visitExprForVars(expr.right, env, crossFile)
            }
            is ExprAst.CallExpr -> {
                visitExprForVars(expr.callee, env, crossFile)
                expr.args.forEach { visitExprForVars(it, env, crossFile) }
                expr.namedArgs.forEach { visitExprForVars(it.value, env, crossFile) }
            }
            is ExprAst.MemberCall -> {
                visitExprForVars(expr.target, env, crossFile)
                expr.args.forEach { visitExprForVars(it, env, crossFile) }
                expr.namedArgs.forEach { visitExprForVars(it.value, env, crossFile) }
            }
            is ExprAst.SafeMemberCall -> {
                visitExprForVars(expr.target, env, crossFile)
                expr.args.forEach { visitExprForVars(it, env, crossFile) }
            }
            is ExprAst.AwaitExpr -> {
                expr.netRequestCall?.let { visitExprForVars(it, env, crossFile) }
                expr.suspendCall?.let { visitExprForVars(it, env, crossFile) }
                expr.tailExpr?.let { visitExprForVars(it, env, crossFile) }
            }
            is ExprAst.NetRequestCall -> {
                expr.payload.forEach { visitExprForVars(it.value, env, crossFile) }
                expr.callback?.let { visitExprForVars(it, env, crossFile) }
                expr.timeoutMs?.let { visitExprForVars(it, env, crossFile) }
            }
            is ExprAst.NetSubscribeCall -> visitExprForVars(expr.handler, env, crossFile)
            is ExprAst.MemberAccess -> visitExprForVars(expr.target, env, crossFile)
            is ExprAst.SafeMemberAccess -> visitExprForVars(expr.target, env, crossFile)
            // M10 Stage 6：`target!!` 仅穿透 target 子表达式
            is ExprAst.NonNullAssert -> visitExprForVars(expr.target, env, crossFile)
            is ExprAst.IndexExpr -> {
                visitExprForVars(expr.target, env, crossFile); visitExprForVars(expr.index, env, crossFile)
            }
            is ExprAst.RangeExpr -> {
                visitExprForVars(expr.start, env, crossFile); visitExprForVars(expr.endValue, env, crossFile)
            }
            is ExprAst.ListLiteralExpr -> expr.elements.forEach { visitExprForVars(it, env, crossFile) }
            is ExprAst.MapLiteralExpr -> expr.entries.forEach { visitExprForVars(it.value, env, crossFile) }
            is ExprAst.BuiltinCallExpr -> expr.args.forEach { visitExprForVars(it, env, crossFile) }
            is ExprAst.IsExpr -> visitExprForVars(expr.target, env, crossFile)
            is ExprAst.Interp -> expr.parts.forEach {
                if (it is ExprAst.Interp.InterpPart.ExprPart) visitExprForVars(it.expr, env, crossFile)
            }
            // 叶子节点：无嵌套子表达式
            is ExprAst.Literal,
            is ExprAst.NullLit,
            is ExprAst.IdentRef,
            is ExprAst.LocalRef,
            is ExprAst.StateRef,
            is ExprAst.SessionRef,
            is ExprAst.PersistRef,
            is ExprAst.DerivedRef,
            is ExprAst.PropsRef,
            is ExprAst.ThemeRef,
            is ExprAst.FunRefExpr,
            -> Unit
        }
    }

    /**
     * 旧公共 API：表达式类型推断（不带跨文件类型表）。向后兼容用，内部委托 3 参版本。
     */
    fun inferExpr(expr: ExprAst, env: Map<String, IrType> = emptyMap()): IrType =
        inferExpr(expr, env, ProjectTypeTable.EMPTY)

    /**
     * 表达式类型推断；不可推断时返回 [IrType.AnyT]。
     *
     * [env] 提供已知 LocalRef 名 → IrType 的映射；通常由 [infer] 内部维护，外部直接调用
     * 时可传 emptyMap。
     *
     * [crossFileTypes] 提供跨文件 fun 签名与 data 字段表（M10 Stage 3）；env 未命中且为
     * CallExpr / MemberAccess(target=DataInstance) 时反查它得到精确类型。
     *
     * 分支按"字面量 → 引用 → 一元/二元 → 控制流 → 调用 / 后缀链 → 兜底"分组排列，让阅读
     * 时能快速定位某一类表达式的处理位置。
     *
     * **循环依赖防护**：MemberAccess 反查 dataFields 时会递归 inferExpr 解析 target，
     * 由于 DataInstance 只通过名字承载、不持有字段引用，这里的递归不会重新进入"同一
     * className 的同一字段"链路；最坏情况 target 又是 DataInstance(C2)、C2 又有 DataInstance(C3)
     * 的字段，每一层 className 不同，递归总会因为字段命中或访问空字符串/AnyT 而终止。
     */
    fun inferExpr(
        expr: ExprAst,
        env: Map<String, IrType>,
        crossFileTypes: ProjectTypeTable,
    ): IrType = when (expr) {
        // ----- 字面量 -----
        is ExprAst.Literal.NumberLit -> IrType.NumberT
        is ExprAst.Literal.StringLit -> IrType.StringT
        is ExprAst.Literal.BoolLit -> IrType.BooleanT
        is ExprAst.Interp -> IrType.StringT
        is ExprAst.NullLit -> IrType.Nullable(IrType.AnyT)
        is ExprAst.ListLiteralExpr -> {
            // 空列表退化为裸 ListT()（元素类型 AnyT）；非空列表合并各元素类型作为泛型参数。
            if (expr.elements.isEmpty()) IrType.ListT()
            else {
                val elemTypes = expr.elements.map { inferExpr(it, env, crossFileTypes) }
                val merged = elemTypes.reduce { a, b -> mergeBranchTypes(a, b) }
                IrType.ListT(merged)
            }
        }
        is ExprAst.MapLiteralExpr -> {
            // DSL 仅支持字符串 key（语法层保证），key 直接给 StringT；value 由各 entry 合并推断。
            if (expr.entries.isEmpty()) IrType.MapT()
            else {
                val valueTypes = expr.entries.map { inferExpr(it.value, env, crossFileTypes) }
                val merged = valueTypes.reduce { a, b -> mergeBranchTypes(a, b) }
                IrType.MapT(IrType.StringT, merged)
            }
        }
        // Range 元素是 Number，迭代器形态承载为 ListT(NumberT)。
        is ExprAst.RangeExpr -> IrType.ListT(IrType.NumberT)

        // ----- 引用 -----
        is ExprAst.LocalRef -> {
            // env 命中优先；未命中时本 stage 仍降级为 AnyT——函数引用本身不是函数类型，
            // 仅函数调用（CallExpr）路径才会消费 crossFileTypes.functions 拿到返回值类型。
            env[expr.name] ?: IrType.AnyT
        }

        // ----- 一元 / 二元 -----
        is ExprAst.UnaryOp -> when (expr.op) {
            "!" -> IrType.BooleanT
            "-" -> IrType.NumberT
            else -> IrType.AnyT
        }
        is ExprAst.BinaryOp -> inferBinary(expr, env, crossFileTypes)
        is ExprAst.IsExpr -> IrType.BooleanT

        // ----- 控制流 -----
        is ExprAst.Ternary -> mergeBranchTypes(
            inferExpr(expr.thenExpr, env, crossFileTypes),
            inferExpr(expr.elseExpr, env, crossFileTypes),
        )
        is ExprAst.Elvis -> mergeBranchTypes(
            unwrapNullable(inferExpr(expr.left, env, crossFileTypes)),
            inferExpr(expr.right, env, crossFileTypes),
        )
        is ExprAst.IfExpr -> mergeBranchTypes(
            inferStmtListType(expr.thenBody, env, crossFileTypes),
            inferStmtListType(expr.elseBody, env, crossFileTypes),
        )
        is ExprAst.WhenExpr -> inferWhenType(expr, env, crossFileTypes)

        // ----- 调用 / 后缀链 -----
        is ExprAst.CallExpr -> {
            // M10 Stage 3：CallExpr 之前走 else 兜底 AnyT；现在当 callee 是 IdentRef / LocalRef
            // 且名字命中跨文件 fun 表时，返回签名声明的 returnType。
            val calleeName = when (val c = expr.callee) {
                is ExprAst.IdentRef -> c.name
                is ExprAst.LocalRef -> c.name
                else -> null
            }
            if (calleeName != null) {
                crossFileTypes.functions[calleeName]?.returnType ?: IrType.AnyT
            } else {
                IrType.AnyT
            }
        }
        is ExprAst.MemberAccess -> {
            // M10 Stage 3：MemberAccess 之前走 else 兜底 AnyT；现在递归推断 target 类型，
            // 若是 DataInstance(className)，按 dataFields 反查精确字段类型。
            val targetType = inferExpr(expr.target, env, crossFileTypes)
            if (targetType is IrType.DataInstance) {
                crossFileTypes.dataFields[targetType.className]?.get(expr.member) ?: IrType.AnyT
            } else {
                IrType.AnyT
            }
        }
        is ExprAst.MemberCall -> inferMemberCall(expr)
        is ExprAst.SafeMemberCall -> {
            val inner = inferMemberCall(asMemberCall(expr))
            IrType.Nullable(inner)
        }
        is ExprAst.SafeMemberAccess -> IrType.Nullable(IrType.AnyT)
        is ExprAst.NonNullAssert -> {
            // M10 Stage 6：`target!!` 编译期窄化——target 为 Nullable(T) 时返回 T，
            // 非 Nullable 形态原样返回（unwrapNullable 容错）。
            val targetType = inferExpr(expr.target, env, crossFileTypes)
            unwrapNullable(targetType)
        }
        is ExprAst.IndexExpr -> {
            // 利用容器泛型实参回填索引访问的元素类型；未知或非容器目标退化为 AnyT。
            val targetType = inferExpr(expr.target, env, crossFileTypes)
            when (targetType) {
                is IrType.ListT -> targetType.arg
                is IrType.MapT -> targetType.value
                else -> IrType.AnyT
            }
        }

        // ----- 兜底 -----
        else -> IrType.AnyT
    }

    /** 将 SafeMemberCall 映射为 MemberCall 签名视角，仅用于末端方法名查表。 */
    private fun asMemberCall(e: ExprAst.SafeMemberCall): ExprAst.MemberCall =
        ExprAst.MemberCall(e.target, e.method, e.args, e.position)

    /** 拆掉 Nullable 包装，让 Elvis 左侧 `a ?: b` 的非空分支类型与右侧合并。 */
    private fun unwrapNullable(t: IrType): IrType = if (t is IrType.Nullable) t.inner else t

    /**
     * 合并两个分支类型，找最具体的公共类型。
     *
     * - 任一为 [IrType.AnyT] → 取另一个（让"已知 + 未知" = "已知"）
     * - 两者相同 → 取该类型
     * - 一侧 `Nullable(AnyT)`（典型来源是 `null` 字面量），另一侧 T → `Nullable(T)`
     *   （让 `if (c) null else 1` 推断为 `Number?`）
     * - 一侧 `Nullable(T)`，另一侧 T（同型非空）→ `Nullable(T)`（保留可空性）
     * - 其它不一致 → [IrType.AnyT]（避免做错误强制合并）
     */
    private fun mergeBranchTypes(a: IrType, b: IrType): IrType {
        if (a == IrType.AnyT) return b
        if (b == IrType.AnyT) return a
        if (a == b) return a
        // null 字面量（Nullable(AnyT)）与具体类型 T 合并 → Nullable(T)。
        // 这是常见模式 `if (c) null else 1` / `var x = if (c) "a" else null` 的核心场景。
        if (a is IrType.Nullable && a.inner == IrType.AnyT && b !is IrType.Nullable) {
            return IrType.Nullable(b)
        }
        if (b is IrType.Nullable && b.inner == IrType.AnyT && a !is IrType.Nullable) {
            return IrType.Nullable(a)
        }
        // Nullable(T) 与 T → Nullable(T)（保留可空性）
        if (a is IrType.Nullable && a.inner == b) return a
        if (b is IrType.Nullable && b.inner == a) return b
        // 其它不一致 → AnyT
        return IrType.AnyT
    }

    /**
     * if 表达式 / when 分支体的类型 = 最后一条 ExprStmt / ReturnStmt 的表达式类型。
     *
     * 当 body 为空或末尾不是表达式 / return 时退化为 AnyT。
     *
     * TODO(v2)：末尾是 AssignStmt 等无返回值语句时应推断为 [IrType.UnitT]（更精确），
     * 而不是当前的 AnyT 兜底；保留现状是因为 DSL 的 if 表达式末尾几乎都是字面量 / 调用，
     * 实际未见 AssignStmt 收尾的真实用法。等出现需求时再调整。
     */
    private fun inferStmtListType(
        stmts: List<StmtAst>,
        env: Map<String, IrType>,
        crossFile: ProjectTypeTable,
    ): IrType {
        val last = stmts.lastOrNull() ?: return IrType.AnyT
        return when (last) {
            is StmtAst.ExprStmt -> inferExpr(last.call, env, crossFile)
            is StmtAst.ReturnStmt -> last.value?.let { inferExpr(it, env, crossFile) } ?: IrType.UnitT
            else -> IrType.AnyT
        }
    }

    /**
     * when 表达式：把所有分支体（含 elseBody）的末端表达式类型逐一合并。
     */
    private fun inferWhenType(
        expr: ExprAst.WhenExpr,
        env: Map<String, IrType>,
        crossFile: ProjectTypeTable,
    ): IrType {
        var merged: IrType = IrType.AnyT
        var first = true
        for (br in expr.branches) {
            val brType = inferStmtListType(br.body, env, crossFile)
            merged = if (first) {
                first = false
                brType
            } else {
                mergeBranchTypes(merged, brType)
            }
        }
        expr.elseBody?.let {
            val elseType = inferStmtListType(it, env, crossFile)
            merged = if (first) elseType else mergeBranchTypes(merged, elseType)
        }
        return merged
    }

    private fun inferBinary(
        expr: ExprAst.BinaryOp,
        env: Map<String, IrType>,
        crossFile: ProjectTypeTable,
    ): IrType {
        return when (expr.op) {
            "+" -> {
                val l = inferExpr(expr.left, env, crossFile)
                val r = inferExpr(expr.right, env, crossFile)
                if (l == IrType.StringT || r == IrType.StringT) IrType.StringT else IrType.NumberT
            }
            "-", "*", "/", "%" -> IrType.NumberT
            "<", ">", "<=", ">=", "==", "!=", "&&", "||" -> IrType.BooleanT
            else -> IrType.AnyT
        }
    }

    /**
     * 识别 typed helper 形态：`<target>.number|string|bool|map|list(...)`。
     *
     * 仅看末端方法名；中间链路的具体形态由 parser/语义层保证，此处不做白名单。
     *
     * 同时识别 List/Map helper 中"返回类型可静态确定"的方法名，便于链式表达式的类型守卫：
     * - 查询返回 Boolean：isEmpty / contains / containsKey / has
     * - 查询返回 Number：indexOf / size
     * - 查询返回 List：keys
     * - 不可变变换返回原集合类型：with / without / removed / removedAt /
     *   added / appended / prepended / take（List 形态返回 List；Map 形态返回 Map）
     * - 可变操作 add / put / removeAt / remove 返回值类型不固定（null / Boolean / 被移除元素），
     *   保持 AnyT，避免误导链式推断。
     *
     * 注：当前推断器不区分 List 与 Map 的来源链路，对 added/removed/take 等 list-only 方法
     * 与 with/without 等 map-only 方法分别推断为 ListT / MapT，实际目标类型由运行时分派校验。
     */
    private fun inferMemberCall(expr: ExprAst.MemberCall): IrType = when (expr.method) {
        // typed payload helper
        "number" -> IrType.NumberT
        "string" -> IrType.StringT
        "bool" -> IrType.BooleanT
        // 嵌套 Map / List 取值仅按容器类型回报，不再下钻泛型参数（typed helper 链没有携带元素类型）。
        "map" -> IrType.MapT()
        "list" -> IrType.ListT()
        // 集合查询
        "isEmpty", "contains", "containsKey", "has" -> IrType.BooleanT
        "indexOf", "size" -> IrType.NumberT
        "keys" -> IrType.ListT()
        // List 不可变变换：返回 List
        "added", "appended", "prepended", "take", "removed", "removedAt" -> IrType.ListT()
        // Map 不可变变换：返回 Map
        "with", "without" -> IrType.MapT()
        // 可变操作及其它：返回类型不固定
        else -> IrType.AnyT
    }
}
